// Skeleton.js - Skeleton loading components
// Features: Card, Table, Chart, Text, and custom skeletons

import React from 'react';
import './Skeleton.css';

/**
 * Base Skeleton Component
 * @param {string} [variant] - 'text' | 'circular' | 'rectangular'
 * @param {string|number} [width] - Width (default: 100%)
 * @param {string|number} [height] - Height (default: based on variant)
 * @param {string} [className] - Additional class name
 * @param {boolean} [animate] - Enable shimmer animation (default: true)
 */
export function Skeleton({
    variant = 'text',
    width,
    height,
    className = '',
    animate = true,
    style = {}
}) {
    const getDefaultHeight = () => {
        switch (variant) {
            case 'text':
                return '1em';
            case 'circular':
                return width || '40px';
            case 'rectangular':
                return '100px';
            default:
                return '1em';
        }
    };

    return (
        <div
            className={`skeleton skeleton-${variant} ${animate ? 'skeleton-animate' : ''} ${className}`}
            style={{
                width: width || '100%',
                height: height || getDefaultHeight(),
                ...style
            }}
            aria-hidden="true"
        />
    );
}

/**
 * Skeleton Card - Full card placeholder
 * @param {boolean} [showImage] - Show image placeholder
 * @param {number} [lines] - Number of text lines (default: 3)
 */
export function SkeletonCard({ showImage = false, lines = 3, className = '' }) {
    return (
        <div className={`skeleton-card ${className}`}>
            {showImage && (
                <Skeleton variant="rectangular" height="120px" className="skeleton-card-image" />
            )}
            <div className="skeleton-card-content">
                <Skeleton variant="text" width="60%" height="1.2em" />
                {Array.from({ length: lines }).map((_, i) => (
                    <Skeleton
                        key={i}
                        variant="text"
                        width={i === lines - 1 ? '40%' : '100%'}
                        height="0.9em"
                    />
                ))}
            </div>
        </div>
    );
}

/**
 * Skeleton Stat Card - For metric/stat display cards
 */
export function SkeletonStatCard({ className = '' }) {
    return (
        <div className={`skeleton-stat-card ${className}`}>
            <Skeleton variant="circular" width="48px" height="48px" />
            <div className="skeleton-stat-content">
                <Skeleton variant="text" width="60px" height="2rem" />
                <Skeleton variant="text" width="80px" height="0.8rem" />
            </div>
        </div>
    );
}

/**
 * Skeleton Table - Table placeholder with rows
 * @param {number} [rows] - Number of rows (default: 5)
 * @param {number} [cols] - Number of columns (default: 4)
 */
export function SkeletonTable({ rows = 5, cols = 4, className = '' }) {
    return (
        <div className={`skeleton-table ${className}`}>
            {/* Header */}
            <div className="skeleton-table-header">
                {Array.from({ length: cols }).map((_, i) => (
                    <Skeleton
                        key={`header-${i}`}
                        variant="text"
                        width={i === 0 ? '30%' : '20%'}
                        height="0.8rem"
                    />
                ))}
            </div>

            {/* Rows */}
            {Array.from({ length: rows }).map((_, rowIndex) => (
                <div key={`row-${rowIndex}`} className="skeleton-table-row">
                    {Array.from({ length: cols }).map((_, colIndex) => (
                        <Skeleton
                            key={`cell-${rowIndex}-${colIndex}`}
                            variant="text"
                            width={colIndex === 0 ? '70%' : '50%'}
                            height="0.9rem"
                        />
                    ))}
                </div>
            ))}
        </div>
    );
}

/**
 * Skeleton Chart - Chart placeholder
 * @param {string} [type] - 'bar' | 'line' | 'pie' | 'area'
 * @param {string} [height] - Chart height
 */
export function SkeletonChart({ type = 'bar', height = '200px', className = '' }) {
    return (
        <div className={`skeleton-chart skeleton-chart-${type} ${className}`} style={{ height }}>
            {type === 'bar' && (
                <div className="skeleton-chart-bars">
                    {[60, 80, 45, 90, 70, 55, 85].map((h, i) => (
                        <Skeleton
                            key={i}
                            variant="rectangular"
                            width="10%"
                            height={`${h}%`}
                            className="skeleton-bar"
                        />
                    ))}
                </div>
            )}

            {type === 'line' && (
                <div className="skeleton-chart-line">
                    <Skeleton variant="rectangular" width="100%" height="2px" className="skeleton-line" />
                </div>
            )}

            {type === 'pie' && (
                <div className="skeleton-chart-pie">
                    <Skeleton variant="circular" width="150px" height="150px" />
                </div>
            )}

            {type === 'area' && (
                <div className="skeleton-chart-area">
                    <Skeleton variant="rectangular" width="100%" height="100%" />
                </div>
            )}
        </div>
    );
}

/**
 * Skeleton Dashboard - Full dashboard layout placeholder
 */
export function SkeletonDashboard({ className = '' }) {
    return (
        <div className={`skeleton-dashboard ${className}`}>
            {/* Top stats row */}
            <div className="skeleton-dashboard-stats">
                <SkeletonStatCard />
                <SkeletonStatCard />
                <SkeletonStatCard />
                <SkeletonStatCard />
            </div>

            {/* Main content grid */}
            <div className="skeleton-dashboard-grid">
                <div className="skeleton-dashboard-main">
                    <SkeletonChart type="area" height="250px" />
                </div>
                <div className="skeleton-dashboard-side">
                    <SkeletonCard lines={4} />
                </div>
            </div>

            {/* Bottom section */}
            <div className="skeleton-dashboard-table">
                <SkeletonTable rows={5} cols={5} />
            </div>
        </div>
    );
}

/**
 * Skeleton Asset Row - For asset list items
 */
export function SkeletonAssetRow({ className = '' }) {
    return (
        <div className={`skeleton-asset-row ${className}`}>
            <Skeleton variant="circular" width="8px" height="8px" />
            <Skeleton variant="text" width="200px" height="1rem" />
            <Skeleton variant="text" width="60px" height="0.9rem" />
            <div className="skeleton-asset-badges">
                <Skeleton variant="rectangular" width="40px" height="20px" />
                <Skeleton variant="rectangular" width="40px" height="20px" />
                <Skeleton variant="rectangular" width="40px" height="20px" />
            </div>
            <Skeleton variant="text" width="80px" height="0.8rem" />
            <Skeleton variant="rectangular" width="60px" height="28px" />
        </div>
    );
}

/**
 * Skeleton Asset List - Multiple asset rows
 * @param {number} [count] - Number of rows (default: 5)
 */
export function SkeletonAssetList({ count = 5, className = '' }) {
    return (
        <div className={`skeleton-asset-list ${className}`}>
            {Array.from({ length: count }).map((_, i) => (
                <SkeletonAssetRow key={i} />
            ))}
        </div>
    );
}

/**
 * Skeleton Text Block - Paragraph placeholder
 * @param {number} [lines] - Number of lines (default: 4)
 */
export function SkeletonText({ lines = 4, className = '' }) {
    return (
        <div className={`skeleton-text ${className}`}>
            {Array.from({ length: lines }).map((_, i) => (
                <Skeleton
                    key={i}
                    variant="text"
                    width={i === lines - 1 ? '60%' : '100%'}
                    height="1em"
                    style={{ marginBottom: '8px' }}
                />
            ))}
        </div>
    );
}

export default Skeleton;
